import { Reducer } from "redux";
import { ApplicationState } from "store";
import {
    SpiralItem,
    RubberBandItem,
    CustomImage,
    CoverOptions,
    ImageCropperOptions,
    ExtraPage,
} from "burde";
import { NavigationComponent, NavigationComponents } from "./constants";

export type UIState = {
    ValidSpirals: Array<SpiralItem>;
    ValidRubberBands: Array<RubberBandItem>;
    ShowLoginModal: boolean;
    ShowSaveDialog: boolean;
    ImageCropperOptions: ImageCropperOptions;
    ShowImageCroper: boolean;
    ShowImageCroperBack: boolean;
    EditableCustomImage: string;
    EditableCustomBackImage: string;
    ImageUploaderErrorText: string;
    ImageUploaderBackErrorText: string;
    LoadFromLocalStorage: boolean;
    ActiveNavigationComponent: NavigationComponent;
    ShowMainMenu: boolean;
    CroppedCustomImage?: CustomImage | null;
    CroppedCustomBackImage?: CustomImage | null;
    ShowUserMenu: boolean;
    CoverOptions: CoverOptions | null;
    FastTrack: boolean;
    CroppedCustomOwnerPageImage: CustomImage | null;
    OwnerPageImageOptions: CoverOptions | null;
    PreviewedExtraPage: ExtraPage | null;
    spiralItem: SpiralItem | null;
    rubberbandItem: RubberBandItem | null;
    thumbnailBlob: Blob | null;
    domain: string;
    uploadedCoverImages: Array<CustomImage>;
    uploadedOwnerPageImages: Array<CustomImage>;
};

enum ActionType {
    UpdateValidSpirals = "UPDATE_VALID_SPIRALS",
    UpdateValidRubberBands = "UPDATE_VALID_RUBBER_BANDS",
    UpdateShowLoginModal = "UPDATE_SHOW_LOGIN_MODAL",
    UpdateShowSaveDialog = "UPDATE_SHOW_SAVE_DIALOG",
    UpdateShowImageCroper = "UPDATE_SHOW_IMAGE_CROPER",
    UpdateShowImageCroperBack = "UPDATE_SHOW_IMAGE_CROPER_BACK",
    UpdateEditableCustomImage = "UPDATE_EDITABLE_CUSTOM_IMAGE",
    UpdateEditableCustomBackImage = "UPDATE_EDITABLE_CUSTOM_BACK_IMAGE",
    UpdateImageUploaderErrorText = "UPDATE_IMAGE_UPLOADER_ERROR_TEXT",
    UpdateImageUploaderBackErrorText = "UPDATE_IMAGE_UPLOADER_BACK_ERROR_TEXT",
    UpdateLoadFromLocalStorage = "UPDATE_LOAD_FROM_LOCAL_STORAGE",
    UpdateActiveNavigationComponent = "UPDATE_ACTIVE_NAVIGATION_COMPONENT",
    UpdateShowMainMenu = "UPDATE_SHOW_MAIN_MENU",
    UpdateCroppedCustomImage = "UPDATE_CROPPED_CUSTOM_IMAGE",
    UpdateCroppedCustomBackImage = "UPDATE_CROPPED_CUSTOM_BACK_IMAGE",
    UpdateShowUserMenu = "UPDATE_SHOW_USER_MENU",
    UpdateNavigationComponent = "UPDATE_NAVIGATION_COMPONENT",
    UpdateCoverOptions = "UPDATE_COVER_OPTIONS",
    SetFastTrack = "SET_FAST_TRACK",
    UpdateImageCropperOptions = "UPDATE_IMAGE_CROPPER_OPTIONS",
    UpdateCroppedCustomOwnerPageImage = "UPDATE_CROPPED_CUSTOM_OWNER_PAGE_IMAGE",
    UpdateOwnerPageImageOptions = "UPDATE_OWNER_PAGE_IMAGE_OPTIONS",
    UpdatePreviewedExtraPage = "UPDATE_PREVIEWED_EXTRA_PAGE",
    UpdateSpiralItem = "UPDATE_SPIRAL_ITEM",
    UpdateRubberbandItem = "UPDATE_RUBBERBAND_ITEM",
    UpdateThumbnailBlob = "UPDATE_THUMBNAIL_BLOB",
    UpdateUploadedCoverImages = "UPDATE_UPLOADED_COVER_IMAGES",
    UpdateUploadedOwnerPageImages = "UPDATE_UPLOADED_OWNER_PAGE_IMAGES",
}

interface UpdateValidSpiralsAction {
    type: ActionType.UpdateValidSpirals;
    collection: Array<SpiralItem>;
}

interface UpdateValidRubberBandsAction {
    type: ActionType.UpdateValidRubberBands;
    collection: Array<RubberBandItem>;
}

interface UpdateShowLoginModalAction {
    type: ActionType.UpdateShowLoginModal;
    value: boolean;
}

interface UpdateShowSaveDialog {
    type: ActionType.UpdateShowSaveDialog;
    value: boolean;
}

interface UpdateShowImageCroper {
    type: ActionType.UpdateShowImageCroper;
    value: boolean;
}

interface UpdateShowImageCroperBack {
    type: ActionType.UpdateShowImageCroperBack;
    value: boolean;
}

interface UpdateEditableCustomImage {
    type: ActionType.UpdateEditableCustomImage;
    value: string;
}

interface UpdateEditableCustomBackImage {
    type: ActionType.UpdateEditableCustomBackImage;
    value: string;
}

interface UpdateImageUploaderErrorText {
    type: ActionType.UpdateImageUploaderErrorText;
    value: string;
}

interface UpdateImageUploaderBackErrorText {
    type: ActionType.UpdateImageUploaderBackErrorText;
    value: string;
}

interface UpdateLoadFromLocalStorage {
    type: ActionType.UpdateLoadFromLocalStorage;
    value: boolean;
}

interface UpdateActiveNavigationComponent {
    type: ActionType.UpdateActiveNavigationComponent;
    value: NavigationComponent;
}

interface UpdateShowMainMenu {
    type: ActionType.UpdateShowMainMenu;
    value: boolean;
}

interface UpdateCroppedCustomImage {
    type: ActionType.UpdateCroppedCustomImage;
    value: CustomImage;
}

interface UpdateCroppedCustomBackImage {
    type: ActionType.UpdateCroppedCustomBackImage;
    value: CustomImage;
}

interface UpdateShowUserMenu {
    type: ActionType.UpdateShowUserMenu;
    value: boolean;
}

interface UpdateNavigationComponent {
    type: ActionType.UpdateNavigationComponent;
    value: NavigationComponent;
}

interface UpdateCoverOptions {
    type: ActionType.UpdateCoverOptions;
    value: CoverOptions;
}

interface SetFastTrack {
    type: ActionType.SetFastTrack;
    value: boolean;
}

interface UpdateImageCropper {
    type: ActionType.UpdateImageCropperOptions;
    value: ImageCropperOptions;
}

interface UpdateCroppedCustomOwnerPageImage {
    type: ActionType.UpdateCroppedCustomOwnerPageImage;
    value: CustomImage | null;
}

interface UpdateOwnerPageImageOptions {
    type: ActionType.UpdateOwnerPageImageOptions;
    value: CoverOptions;
}

interface UpdatePreviewedExtraPage {
    type: ActionType.UpdatePreviewedExtraPage;
    value: ExtraPage | null;
}

interface UpdateSpiralItem {
    type: ActionType.UpdateSpiralItem;
    value: SpiralItem | null;
}

interface UpdateRubberbandItem {
    type: ActionType.UpdateRubberbandItem;
    value: RubberBandItem | null;
}

interface UpdateThumbnailBlob {
    type: ActionType.UpdateThumbnailBlob;
    value: Blob | null;
}

interface UpdateUploadedCoverImages {
    type: ActionType.UpdateUploadedCoverImages;
    value: Array<CustomImage>;
}

interface UpdateUploadedOwnerPageImages {
    type: ActionType.UpdateUploadedOwnerPageImages;
    value: Array<CustomImage>;
}

type UIAction =
    | UpdateValidSpiralsAction
    | UpdateValidRubberBandsAction
    | UpdateShowLoginModalAction
    | UpdateShowSaveDialog
    | UpdateShowImageCroper
    | UpdateShowImageCroperBack
    | UpdateEditableCustomImage
    | UpdateEditableCustomBackImage
    | UpdateImageUploaderErrorText
    | UpdateImageUploaderBackErrorText
    | UpdateLoadFromLocalStorage
    | UpdateActiveNavigationComponent
    | UpdateShowMainMenu
    | UpdateCroppedCustomImage
    | UpdateCroppedCustomBackImage
    | UpdateShowUserMenu
    | UpdateNavigationComponent
    | UpdateCoverOptions
    | SetFastTrack
    | UpdateImageCropper
    | UpdateCroppedCustomOwnerPageImage
    | UpdateOwnerPageImageOptions
    | UpdatePreviewedExtraPage
    | UpdateSpiralItem
    | UpdateRubberbandItem
    | UpdateThumbnailBlob
    | UpdateUploadedCoverImages
    | UpdateUploadedOwnerPageImages;

export const actionCreators = {
    updateValidSpirals: (collection: Array<SpiralItem>) =>
        <UpdateValidSpiralsAction>{
            type: ActionType.UpdateValidSpirals,
            collection,
        },
    updateValidRubberBands: (collection: Array<RubberBandItem>) =>
        <UpdateValidRubberBandsAction>{
            type: ActionType.UpdateValidRubberBands,
            collection,
        },
    updateShowLoginModal: (value: boolean) =>
        <UpdateShowLoginModalAction>{
            type: ActionType.UpdateShowLoginModal,
            value,
        },
    UpdateShowSaveDialog: (value: boolean) =>
        <UpdateShowSaveDialog>{
            type: ActionType.UpdateShowSaveDialog,
            value,
        },
    UpdateShowImageCroper: (value: boolean) =>
        <UpdateShowImageCroper>{
            type: ActionType.UpdateShowImageCroper,
            value,
        },
    UpdateShowImageCroperBack: (value: boolean) =>
        <UpdateShowImageCroperBack>{
            type: ActionType.UpdateShowImageCroperBack,
            value,
        },
    UpdateEditableCustomImage: (value: string) =>
        <UpdateEditableCustomImage>{
            type: ActionType.UpdateEditableCustomImage,
            value,
        },
    UpdateEditableCustomBackImage: (value: string) =>
        <UpdateEditableCustomBackImage>{
            type: ActionType.UpdateEditableCustomBackImage,
            value,
        },
    UpdateImageUploaderErrorText: (value: string) =>
        <UpdateImageUploaderErrorText>{
            type: ActionType.UpdateImageUploaderErrorText,
            value,
        },
    UpdateImageUploaderBackErrorText: (value: string) =>
        <UpdateImageUploaderBackErrorText>{
            type: ActionType.UpdateImageUploaderBackErrorText,
            value,
        },
    UpdateLoadFromLocalStorage: (value: boolean) =>
        <UpdateLoadFromLocalStorage>{
            type: ActionType.UpdateLoadFromLocalStorage,
            value,
        },
    UpdateActiveNavigationComponent: (value: NavigationComponent) =>
        <UpdateActiveNavigationComponent>{
            type: ActionType.UpdateActiveNavigationComponent,
            value,
        },
    UpdateShowMainMenu: (value: Boolean) =>
        <UpdateShowMainMenu>{
            type: ActionType.UpdateShowMainMenu,
            value,
        },
    UpdateCroppedCustomImage: (value: CustomImage) =>
        <UpdateCroppedCustomImage>{
            type: ActionType.UpdateCroppedCustomImage,
            value,
        },
    UpdateCroppedCustomBackImage: (value: CustomImage) =>
        <UpdateCroppedCustomBackImage>{
            type: ActionType.UpdateCroppedCustomBackImage,
            value,
        },
    UpdateShowUserMenu: (value: boolean) =>
        <UpdateShowUserMenu>{
            type: ActionType.UpdateShowUserMenu,
            value,
        },
    UpdateNavigationComponent: (value: NavigationComponent) =>
        <UpdateNavigationComponent>{
            type: ActionType.UpdateNavigationComponent,
            value,
        },
    UpdateCoverOptions: (value: CoverOptions) =>
        <UpdateCoverOptions>{
            type: ActionType.UpdateCoverOptions,
            value,
        },
    SetFastTrack: (value: boolean) =>
        <SetFastTrack>{
            type: ActionType.SetFastTrack,
            value,
        },
    UpdateImageCropper: (value: ImageCropperOptions) =>
        <UpdateImageCropper>{
            type: ActionType.UpdateImageCropperOptions,
            value,
        },
    updateCroppedCustomOwnerPageImage: (value: CustomImage | null) =>
        <UpdateCroppedCustomOwnerPageImage>{
            type: ActionType.UpdateCroppedCustomOwnerPageImage,
            value,
        },
    updateOwnerPageImageOptions: (value: CoverOptions) =>
        <UpdateOwnerPageImageOptions>{
            type: ActionType.UpdateOwnerPageImageOptions,
            value,
        },
    updatePreviewedExtraPage: (value: ExtraPage | null) =>
        <UpdatePreviewedExtraPage>{
            type: ActionType.UpdatePreviewedExtraPage,
            value,
        },
    updateSpiralItem: (value: SpiralItem | null) =>
        <UpdateSpiralItem>{
            type: ActionType.UpdateSpiralItem,
            value,
        },
    updateRubberbandItem: (value: RubberBandItem | null) =>
        <UpdateRubberbandItem>{
            type: ActionType.UpdateRubberbandItem,
            value,
        },
    updateThumbnailBlob: (value: Blob | null) =>
        <UpdateThumbnailBlob>{
            type: ActionType.UpdateThumbnailBlob,
            value,
        },
    updateUploadedCoverImages: (value: Array<CustomImage>) =>
        <UpdateUploadedCoverImages>{
            type: ActionType.UpdateUploadedCoverImages,
            value,
        },
    updateUploadedOwnerPageImages: (value: Array<CustomImage>) =>
        <UpdateUploadedOwnerPageImages>{
            type: ActionType.UpdateUploadedOwnerPageImages,
            value,
        },
};

export const initialState: UIState = {
    ValidSpirals: [],
    ValidRubberBands: [],
    ShowLoginModal: false,
    ShowSaveDialog: false,
    ImageCropperOptions: {
        isVisible: false,
        imageUrl: "",
        onSaveCallback: null,
    },
    ShowImageCroper: false,
    ShowImageCroperBack: false,
    EditableCustomImage: "",
    EditableCustomBackImage: "",
    ImageUploaderErrorText: "",
    ImageUploaderBackErrorText: "",
    LoadFromLocalStorage: true,
    ActiveNavigationComponent: NavigationComponents[0],
    ShowMainMenu: false,
    CroppedCustomImage: null,
    CroppedCustomBackImage: null,
    ShowUserMenu: false,
    CoverOptions: null,
    FastTrack: false,
    CroppedCustomOwnerPageImage: null,
    OwnerPageImageOptions: null,
    PreviewedExtraPage: null,
    spiralItem: null,
    rubberbandItem: null,
    thumbnailBlob: null,
    domain: "",
    uploadedCoverImages: [],
    uploadedOwnerPageImages: [],
};

export const getValidRubberBands = (state: ApplicationState) =>
    state.ui.ValidRubberBands;

export const getValidSpirals = (state: ApplicationState) =>
    state.ui.ValidSpirals;

export const getShowLoginModal = (state: ApplicationState) =>
    state.ui.ShowLoginModal;

export const getShowSaveDialog = (state: ApplicationState) =>
    state.ui.ShowSaveDialog;

export const getShowImageCroper = (state: ApplicationState) =>
    state.ui.ShowImageCroper;

export const getShowImageCroperBack = (state: ApplicationState) =>
    state.ui.ShowImageCroperBack;

export const getEditableCustomImage = (state: ApplicationState) =>
    state.ui.EditableCustomImage;

export const getEditableCustomBackImage = (state: ApplicationState) =>
    state.ui.EditableCustomBackImage;

export const getImageUploaderErrorText = (state: ApplicationState) =>
    state.ui.ImageUploaderErrorText;

export const getImageUploaderBackErrorText = (state: ApplicationState) =>
    state.ui.ImageUploaderBackErrorText;

export const getLoadFromLocalStorage = (state: ApplicationState) =>
    state.ui.LoadFromLocalStorage;

export const getActiveNavigationComponent = (state: ApplicationState) =>
    state.ui.ActiveNavigationComponent;

export const getShowMainMenu = (state: ApplicationState) =>
    state.ui.ShowMainMenu;

export const getCroppedCustomImage = (state: ApplicationState) =>
    state.ui.CroppedCustomImage;

export const getCroppedCustomBackImage = (state: ApplicationState) =>
    state.ui.CroppedCustomBackImage;

export const getShowUserMenu = (state: ApplicationState) =>
    state.ui.ShowUserMenu;

export const getCoverOptions = (state: ApplicationState) =>
    state.ui.CoverOptions;

export const getImageResources = (state: ApplicationState) =>
    (state.ui.CoverOptions && state.ui.CoverOptions.imageResources) || null;

export const getFastTrackStatus = (state: ApplicationState) =>
    state.ui.FastTrack;

export const getCropperOptions = (state: ApplicationState) =>
    state.ui.ImageCropperOptions;

export const getCroppedCustomOwnerPageImage = (state: ApplicationState) =>
    state.ui.CroppedCustomOwnerPageImage;

export const getOwnerPageImageOptions = (state: ApplicationState) =>
    state.ui.OwnerPageImageOptions;

export const getOwnerPageImageResources = (state: ApplicationState) =>
    (state.ui.OwnerPageImageOptions &&
        state.ui.OwnerPageImageOptions.imageResources) ||
    null;

export const getPreviewedExtraPage = (state: ApplicationState) =>
    state.ui.PreviewedExtraPage;

export const getSpiralItem = (state: ApplicationState) => state.ui.spiralItem;

export const getRubberbandItem = (state: ApplicationState) =>
    state.ui.rubberbandItem;

export const getThumbnailBlob = (state: ApplicationState) =>
    state.ui.thumbnailBlob;

export const getDomain = (state: ApplicationState) => state.ui.domain;

export const getUploadedCoverImages = (state: ApplicationState) =>
    state.ui.uploadedCoverImages;

export const getUploadedOwnerPageImages = (state: ApplicationState) =>
    state.ui.uploadedOwnerPageImages;

export const reducer: Reducer<UIState, any> = (
    state: UIState = initialState,
    action: UIAction,
) => {
    if (!action) {
        return state;
    }

    switch (action.type) {
        case ActionType.UpdateValidSpirals:
            return {
                ...state,
                ValidSpirals: action.collection,
            };
        case ActionType.UpdateValidRubberBands:
            return {
                ...state,
                ValidRubberBands: action.collection,
            };
        case ActionType.UpdateShowLoginModal:
            return {
                ...state,
                ShowLoginModal: action.value,
            };
        case ActionType.UpdateShowSaveDialog:
            return {
                ...state,
                ShowSaveDialog: action.value,
            };
        case ActionType.UpdateShowImageCroper:
            return {
                ...state,
                ShowImageCroper: action.value,
            };
        case ActionType.UpdateShowImageCroperBack:
            return {
                ...state,
                ShowImageCroperBack: action.value,
            };
        case ActionType.UpdateEditableCustomImage:
            return {
                ...state,
                EditableCustomImage: action.value,
            };
        case ActionType.UpdateEditableCustomBackImage:
            return {
                ...state,
                EditableCustomBackImage: action.value,
            };
        case ActionType.UpdateImageUploaderErrorText:
            return {
                ...state,
                ImageUploaderErrorText: action.value,
            };
        case ActionType.UpdateImageUploaderBackErrorText:
            return {
                ...state,
                ImageUploaderBackErrorText: action.value,
            };
        case ActionType.UpdateLoadFromLocalStorage:
            return {
                ...state,
                LoadFromLocalStorage: action.value,
            };
        case ActionType.UpdateActiveNavigationComponent:
            return {
                ...state,
                ActiveNavigationComponent: action.value,
            };
        case ActionType.UpdateShowMainMenu:
            return {
                ...state,
                ShowMainMenu: action.value,
            };
        case ActionType.UpdateCroppedCustomImage:
            return {
                ...state,
                CroppedCustomImage: action.value,
            };
        case ActionType.UpdateCroppedCustomBackImage:
            return {
                ...state,
                CroppedCustomBackImage: action.value,
            };
        case ActionType.UpdateShowUserMenu:
            return {
                ...state,
                ShowUserMenu: action.value,
            };
        case ActionType.UpdateNavigationComponent:
            return {
                ...state,
                ActiveNavigationComponent: action.value,
            };
        case ActionType.UpdateCoverOptions:
            return {
                ...state,
                CoverOptions: action.value,
            };
        case ActionType.SetFastTrack:
            return {
                ...state,
                FastTrack: action.value,
            };
        case ActionType.UpdateImageCropperOptions:
            return {
                ...state,
                ImageCropperOptions: action.value,
            };
        case ActionType.UpdateCroppedCustomOwnerPageImage:
            return {
                ...state,
                CroppedCustomOwnerPageImage: action.value,
            };
        case ActionType.UpdateOwnerPageImageOptions:
            return {
                ...state,
                OwnerPageImageOptions: action.value,
            };
        case ActionType.UpdatePreviewedExtraPage:
            return {
                ...state,
                PreviewedExtraPage: action.value,
            };
        case ActionType.UpdateSpiralItem:
            return {
                ...state,
                spiralItem: action.value,
            };
        case ActionType.UpdateRubberbandItem:
            return {
                ...state,
                rubberbandItem: action.value,
            };
        case ActionType.UpdateThumbnailBlob:
            return {
                ...state,
                thumbnailBlob: action.value,
            };
        case ActionType.UpdateUploadedCoverImages:
            return {
                ...state,
                uploadedCoverImages: action.value,
            };
        case ActionType.UpdateUploadedOwnerPageImages:
            return {
                ...state,
                uploadedOwnerPageImages: action.value,
            };
    }

    // todo, add actions etc
    return state;
};
